import { CustomAppEvents } from 'core/common/entities';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { AnalyticsPixelId, FacebookEventNames, FacebookMethods } from '../../entities';
import { FacebookService } from '../../interfaces/FacebookService';

const noop: FacebookMethods & { isAvailable: false } = {
  isAvailable: false as const,
  revokeConsent: () => {},
  grantConsent: () => {},
  init: () => {},
  track: () => {},
};

export class HttpFacebookService implements FacebookService {
  private readonly passedPixelIds: Array<AnalyticsPixelId> = [];
  private initializedPixelIds: Array<AnalyticsPixelId> = [];

  constructor(
    private readonly cookieConsentService: CookieConsentService,
    pixelIds?: Array<AnalyticsPixelId>,
  ) {
    this.passedPixelIds = !!pixelIds?.length ? pixelIds : [];
    this.cookieConsentService.addConsentLoadListener(this.onConsentLoad(this.passedPixelIds));
    this.cookieConsentService.addConsentChangeListener(this.onConsentChange(this.passedPixelIds));
  }

  isFbqRequired(): boolean {
    return !!this.passedPixelIds.length;
  }

  isFbqAvailable(): boolean {
    return this.fbq.isAvailable;
  }

  track(name: FacebookEventNames, orderId: string, params?: Record<string, unknown>) {
    this.fbq.track(name, params, { eventID: orderId });
  }

  private get fbq(): FacebookMethods & { isAvailable: boolean } {
    if (!checkIsClientSide() || !window.fbq) {
      return noop;
    }

    const fbq = window.fbq;

    return {
      isAvailable: true as const,
      revokeConsent: () => fbq('consent', 'revoke'),
      grantConsent: () => fbq('consent', 'grant'),
      init: (id: string) => fbq('init', id),
      track: (
        name: FacebookEventNames,
        params?: Record<string, unknown>,
        options?: { eventID: string },
      ) => fbq('track', name, params, options),
    };
  }

  private onConsentLoad =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
      }
    };

  private onConsentChange =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
        return;
      }

      this.fbq.revokeConsent();
    };

  private initializePixels(pixels: Array<AnalyticsPixelId>) {
    if (this.fbq.isAvailable) {
      this.doInitializePixels(pixels);
      return;
    }

    document.addEventListener(
      CustomAppEvents.FACEBOOK_PIXEL_LOADED,
      () => {
        this.doInitializePixels(pixels);
      },
      { once: true },
    );
  }

  private doInitializePixels(pixelsToInitialize: Array<AnalyticsPixelId>) {
    this.fbq.grantConsent();
    pixelsToInitialize.forEach((pixel) => {
      if (this.initializedPixelIds.includes(pixel)) {
        return;
      }

      this.fbq.init(pixel);
      this.fbq.track(FacebookEventNames.PAGE_VIEW);
      this.initializedPixelIds.push(pixel);
    });
  }
}
